import React, { useState, useRef } from 'react';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Divider,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import type { SelectChangeEvent } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import api from '../services/api';
import { AustralianAddressAutocomplete } from '../components/AustralianAddressAutocomplete';
import { TermsAgreementDialog } from '../components/TermsAgreementDialog';
import { wouldProduceReservedSlug } from '../constants/reservedSlugs';
import BrandMark from '../components/BrandMark';

const normalizeDomain = (value: string) => {
  if (!value) return '';
  let url = value.trim();
  if (!/^https?:\/\//i.test(url)) {
    url = `https://${url}`;
  }
  try {
    const hostname = new URL(url).hostname.toLowerCase();
    return hostname.startsWith('www.') ? hostname.slice(4) : hostname;
  } catch {
    return '';
  }
};

const getEmailDomain = (email: string) => {
  const domain = email?.split('@')[1]?.trim().toLowerCase() || '';
  return domain.startsWith('www.') ? domain.slice(4) : domain;
};

// Personal/free email domains not allowed for registration (must use business email)
const PERSONAL_EMAIL_DOMAINS = new Set([
  'gmail.com', 'googlemail.com', 'hotmail.com', 'hotmail.co.uk', 'hotmail.com.au',
  'live.com', 'live.com.au', 'outlook.com', 'outlook.com.au', 'msn.com',
  'yahoo.com', 'yahoo.com.au', 'icloud.com', 'me.com', 'mac.com', 'aol.com',
  'protonmail.com', 'pm.me', 'proton.me', 'zoho.com', 'mail.com', 'yandex.com',
  'gmx.com', 'gmx.net', 'bigpond.com', 'bigpond.net.au', 'optusnet.com.au',
  'optus.com.au', 'tpg.com.au', 'iinet.net.au', 'ymail.com', 'rocketmail.com',
]);

const isPersonalEmailDomain = (email: string): boolean => {
  const domain = getEmailDomain(email);
  if (!domain) return false;
  const base = domain.split('.').slice(-2).join('.');
  return PERSONAL_EMAIL_DOMAINS.has(domain) || PERSONAL_EMAIL_DOMAINS.has(base);
};

const PERSONAL_EMAIL_MSG =
  'Please use a business or professional email. Personal addresses (e.g. Gmail, Outlook, Yahoo) are not accepted.';

type RegistrationType = 'agency' | 'individual';

const AgencyRegister = () => {
  const [registrationType, setRegistrationType] = useState<RegistrationType>('agency');
  const [form, setForm] = useState({
    agencyName: '',
    agencyEmail: '',
    agencyWebsite: '',
    agencyPhone: '',
    agencyAddress: '',
    agencyState: '',
    adminName: '',
    adminEmail: '',
    adminPhone: '',
    acceptTerms: false,
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [termsDialogOpen, setTermsDialogOpen] = useState(false);
  const pendingPayloadRef = useRef<object | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (e: SelectChangeEvent) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const australianStates = [
    { value: 'NSW', label: 'New South Wales' },
    { value: 'VIC', label: 'Victoria' },
    { value: 'QLD', label: 'Queensland' },
    { value: 'WA', label: 'Western Australia' },
    { value: 'SA', label: 'South Australia' },
    { value: 'TAS', label: 'Tasmania' },
    { value: 'ACT', label: 'Australian Capital Territory' },
    { value: 'NT', label: 'Northern Territory' },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const trimmedAdminName = form.adminName.trim();
    const adminNameParts = trimmedAdminName.split(/\s+/);
    if (adminNameParts.length < 2) {
      setError(registrationType === 'individual'
        ? 'Please enter your full name (first and last).'
        : 'Please enter both first and last name for the admin contact.');
      return;
    }

    const isIndividual = registrationType === 'individual';
    if (!isIndividual) {
      if (wouldProduceReservedSlug(form.agencyName)) {
        setError(
          'This agency name conflicts with a reserved path. Please choose a different name (e.g. add "Realty" or "Properties" to differentiate).'
        );
        return;
      }
      const websiteDomain = normalizeDomain(form.agencyWebsite);
      if (!websiteDomain) {
        setError('Please enter a valid agency website URL.');
        return;
      }
      const agencyEmailDomain = getEmailDomain(form.agencyEmail);
      const adminEmailDomain = getEmailDomain(form.adminEmail);
      if (!agencyEmailDomain || !adminEmailDomain) {
        setError('Please enter valid email addresses for the agency and admin contact.');
        return;
      }
      if (isPersonalEmailDomain(form.agencyEmail) || isPersonalEmailDomain(form.adminEmail)) {
        setError(PERSONAL_EMAIL_MSG);
        return;
      }
      const domainsMatchWebsite = (domain: string) =>
        domain === websiteDomain || domain.endsWith(`.${websiteDomain}`);
      if (!domainsMatchWebsite(agencyEmailDomain) || !domainsMatchWebsite(adminEmailDomain)) {
        setError(`Agency and admin email domains must match the website domain (${websiteDomain}).`);
        return;
      }
    } else {
      if (!getEmailDomain(form.adminEmail)) {
        setError('Please enter a valid email address.');
        return;
      }
    }

    const payload = isIndividual
      ? {
          registrationType: 'individual',
          adminName: trimmedAdminName,
          adminEmail: form.adminEmail,
          adminPhone: form.adminPhone,
          address: form.agencyAddress || '',
          state: form.agencyState || '',
          acceptTerms: true,
        }
      : {
          registrationType: 'agency',
          name: form.agencyName,
          email: form.agencyEmail,
          website: form.agencyWebsite,
          phone: form.agencyPhone,
          address: form.agencyAddress,
          state: form.agencyState,
          adminName: trimmedAdminName,
          adminEmail: form.adminEmail,
          adminPhone: form.adminPhone,
          acceptTerms: true,
        };

    if (!form.acceptTerms) {
      pendingPayloadRef.current = payload;
      setTermsDialogOpen(true);
      setError('');
      return;
    }

    performRegistration(payload);
  };

  const performRegistration = async (payload: object) => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      await api.post('/agencies/register', payload);
      setSuccess('Registration successful! Please check your email to verify your account.');
      setSuccessDialogOpen(true);
    } catch (err: any) {
      setError(err.response?.data?.details || err.response?.data?.error || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 4 } }}>
      <Box sx={{ mt: { xs: 2, sm: 4 }, mb: 2 }}>
        <Button
          component={RouterLink}
          to="/"
          variant="outlined"
          size="small"
          sx={{ textTransform: 'none' }}
        >
          ← Back to main page
        </Button>
      </Box>
      <Box sx={{ mt: 2, p: { xs: 2, sm: 4 }, boxShadow: 3, borderRadius: 2, bgcolor: '#fff' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <BrandMark size={40} />
        </Box>
        <Typography variant="h4" gutterBottom align="center">
          {registrationType === 'agency' ? 'Agency Registration' : 'Individual Registration'}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <ToggleButtonGroup
            value={registrationType}
            exclusive
            onChange={(_, v: RegistrationType | null) => v && setRegistrationType(v)}
            aria-label="registration type"
            sx={{ '& .MuiToggleButton-root': { px: 2, py: 1 } }}
          >
            <ToggleButton value="agency" aria-label="agency registration">
              <BusinessIcon sx={{ mr: 1, fontSize: 20 }} />
              For agencies
            </ToggleButton>
            <ToggleButton value="individual" aria-label="individual registration">
              <PersonIcon sx={{ mr: 1, fontSize: 20 }} />
              For solo agents
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <Alert severity="info" sx={{ mb: 2 }}>
          {registrationType === 'agency' ? (
            <><strong>Hurry up — register now</strong> and enjoy 2 months of Agency Premium plan free. No credit card, no commitment. Add up to 5 users (including admin) during your trial.</>
          ) : (
            <><strong>Register as a solo agent</strong> and get 1 month of Elite Individual plan free. No credit card required. Full features except team, commission, and integrations.</>
          )}
        </Alert>
        <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
          <Link component={RouterLink} to="/plans" underline="hover">
            View plans and pricing
          </Link>
        </Typography>
        {error && <Alert severity="error">{error}</Alert>}
        <Dialog
          open={successDialogOpen}
          onClose={() => setSuccessDialogOpen(false)}
          aria-labelledby="registration-success-dialog-title"
          aria-describedby="registration-success-dialog-description"
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 2, p: 1 },
          }}
        >
          <DialogTitle id="registration-success-dialog-title" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleOutlineIcon color="success" sx={{ fontSize: 32 }} />
            Registration complete
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="registration-success-dialog-description" component="div">
              <Typography component="p" sx={{ mb: 1.5 }}>
                {registrationType === 'agency'
                  ? "Thank you for registering your agency. We've sent an email to the admin address you provided."
                  : "Thank you for registering. We've sent an email to the address you provided."
                }
              </Typography>
              <Typography component="p" sx={{ mb: 1.5 }}>
                <strong>Next steps:</strong>
              </Typography>
              <Typography component="ul" sx={{ pl: 2.5, mb: 1.5 }}>
                <li>Check your inbox (and spam folder) for the verification email</li>
                <li>Click the link in the email to set your password</li>
                <li>Log in and start your {registrationType === 'agency' ? '2‑month Agency Premium' : '1‑month Elite Individual'} trial</li>
              </Typography>
              <Typography component="p" color="text.secondary">
                If you don't see the email within a few minutes, contact us at support@workflow.estate.
              </Typography>
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setSuccessDialogOpen(false)} color="primary">
              OK
            </Button>
            <Button component={RouterLink} to="/" variant="contained" color="primary" onClick={() => setSuccessDialogOpen(false)}>
              Back to home
            </Button>
          </DialogActions>
        </Dialog>
        <form onSubmit={handleSubmit}>
          {registrationType === 'agency' ? (
            <>
              <Typography variant="h6" sx={{ mt: 2 }}>Agency Information</Typography>
              <TextField label="Agency Name" name="agencyName" value={form.agencyName} onChange={handleChange} fullWidth margin="normal" required disabled={!!success} />
              <TextField label="Agency Email" name="agencyEmail" value={form.agencyEmail} onChange={handleChange} fullWidth margin="normal" required type="email" disabled={!!success} />
              <TextField label="Agency Website" name="agencyWebsite" value={form.agencyWebsite} onChange={handleChange} fullWidth margin="normal" required type="url" disabled={!!success} />
              <TextField label="Agency Phone Number" name="agencyPhone" value={form.agencyPhone} onChange={handleChange} fullWidth margin="normal" required disabled={!!success} />
              <Box sx={{ mb: 2, mt: 2 }}>
                <AustralianAddressAutocomplete
                  value={form.agencyAddress}
                  onInputChange={(value) => setForm((prev) => ({ ...prev, agencyAddress: value }))}
                  onChange={(addressComponents) => {
                    setForm((prev) => ({
                      ...prev,
                      agencyAddress: addressComponents.formattedAddress || 
                        `${addressComponents.street}, ${addressComponents.city}, ${addressComponents.state} ${addressComponents.postalCode}`,
                      agencyState: addressComponents.state,
                    }));
                  }}
                  label="Agency Address"
                  required
                  disabled={!!success}
                />
              </Box>
              <Divider sx={{ my: 3 }} />
              <Typography variant="h6">Registration Person (Admin)</Typography>
              <TextField label="Full Name" name="adminName" value={form.adminName} onChange={handleChange} fullWidth margin="normal" required disabled={!!success} />
              <TextField label="Email" name="adminEmail" value={form.adminEmail} onChange={handleChange} fullWidth margin="normal" required type="email" disabled={!!success} />
              <TextField label="Phone Number" name="adminPhone" value={form.adminPhone} onChange={handleChange} fullWidth margin="normal" required disabled={!!success} />
            </>
          ) : (
            <>
              <Typography variant="h6" sx={{ mt: 2 }}>Your Information</Typography>
              <TextField label="Full Name" name="adminName" value={form.adminName} onChange={handleChange} fullWidth margin="normal" required placeholder="First and last name" disabled={!!success} />
              <TextField label="Email" name="adminEmail" value={form.adminEmail} onChange={handleChange} fullWidth margin="normal" required type="email" disabled={!!success} />
              <TextField label="Phone Number" name="adminPhone" value={form.adminPhone} onChange={handleChange} fullWidth margin="normal" required disabled={!!success} />
              <Box sx={{ mb: 2, mt: 2 }}>
                <AustralianAddressAutocomplete
                  value={form.agencyAddress}
                  onInputChange={(value) => setForm((prev) => ({ ...prev, agencyAddress: value }))}
                  onChange={(addressComponents) => {
                    setForm((prev) => ({
                      ...prev,
                      agencyAddress: addressComponents.formattedAddress || 
                        `${addressComponents.street}, ${addressComponents.city}, ${addressComponents.state} ${addressComponents.postalCode}`,
                      agencyState: addressComponents.state,
                    }));
                  }}
                  label="Address"
                  disabled={!!success}
                />
              </Box>
            </>
          )}
          <TermsAgreementDialog
            open={termsDialogOpen}
            onClose={() => {
              setTermsDialogOpen(false);
              pendingPayloadRef.current = null;
            }}
            onAccept={() => {
              setForm((prev) => ({ ...prev, acceptTerms: true }));
              setTermsDialogOpen(false);
              const payload = pendingPayloadRef.current;
              pendingPayloadRef.current = null;
              if (payload) {
                performRegistration(payload);
              }
            }}
          />
          <Button type="submit" variant="contained" color="primary" fullWidth sx={{ mt: 2 }} disabled={loading || !!success}>
            {loading ? 'Registering...' : success ? 'Registration Complete' : registrationType === 'agency' ? 'Register Agency' : 'Register as Individual'}
          </Button>
        </form>
      </Box>

      {/* Footer (same as main page) */}
      <Box
        sx={{
          mt: 8,
          mb: 10,
          pt: 4,
          borderTop: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
          pr: { xs: 0, sm: '80px' },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            © {new Date().getFullYear()} CodePulse Pty Ltd
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Contact:{' '}
            <Link href="mailto:support@workflow.estate" underline="hover">
              support@workflow.estate
            </Link>
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            alignItems: { xs: 'flex-start', sm: 'flex-end' },
          }}
        >
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Link component={RouterLink} to="/#features" color="inherit" underline="hover" sx={{ fontSize: '0.875rem' }}>
              Features
            </Link>
            <Link component={RouterLink} to="/support" color="inherit" underline="hover" sx={{ fontSize: '0.875rem' }}>
              Support
            </Link>
            <Link component={RouterLink} to="/contact-sales" color="inherit" underline="hover" sx={{ fontSize: '0.875rem' }}>
              Contact
            </Link>
            <Link component={RouterLink} to="/privacy" color="inherit" underline="hover" sx={{ fontSize: '0.875rem' }}>
              Privacy Policy
            </Link>
            <Link component={RouterLink} to="/terms" color="inherit" underline="hover" sx={{ fontSize: '0.875rem' }}>
              Terms of Service
            </Link>
            <Link component={RouterLink} to="/login" color="inherit" underline="hover" sx={{ fontSize: '0.875rem' }}>
              Log In
            </Link>
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

export default AgencyRegister; 